import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ZoneProvider } from './contexts/ZoneContext';
import { Register } from './components/auth/Register';
import { Login } from './components/auth/Login';
import { ForgotPassword } from './components/auth/ForgotPassword';
import { VerifyEmail } from './components/auth/VerifyEmail';
import { Dashboard } from './components/Dashboard';
import { Profile } from './components/Profile';
import { EmailInfo } from './components/EmailInfo';
import { Upgrade } from './components/Upgrade';
import { SubscriptionSuccess } from './components/SubscriptionSuccess';
import { CheckoutContinue } from './components/CheckoutContinue';
import { TradeSignalsV2 } from './components/TradeSignalsV2';
import TtStockEngines from './components/tt-stock-trader/TtStockEngines';
import TtStockTrades from './components/tt-stock-trader/TtStockTrades';
import TtStockAudit from './components/tt-stock-trader/TtStockAudit';
import TtStockDashboard from './components/tt-stock-trader/TtStockDashboard';
import { FireCalculator } from './components/FireCalculator';
import { LifecycleInvestment } from './components/LifecycleInvestment';
import { NetWorth } from './components/NetWorth';
import { Playbook } from './components/Playbook';
import { BookPage } from './components/BookPage';
import { LinksPage } from './components/LinksPage';
import { DcaTool } from './components/DcaTool';
import { StockSignals } from './components/StockSignals';
import { BucketVisualizer } from './components/BucketVisualizer';
import { FourBucketExplainer } from './components/FourBucketExplainer';
import { FivePillarsExplainer } from './components/FivePillarsExplainer';
import { HelpDocs } from './components/HelpDocs';
import { ContactSupport } from './components/ContactSupport';
import { ChatWidget } from './components/ChatWidget';
import { AlgoEngines } from './components/algo/AlgoEngines';
import { NotchesPage } from './components/notches/NotchesPage';
import { NotchesAudit } from './components/notches/NotchesAudit';
import { NotchesStressTest } from './components/notches/NotchesStressTest';
import { NotchesEventsPage } from './components/notches/NotchesEventsPage';
import { NotchesReportsPage } from './components/notches/NotchesReportsPage';
import { NotchesDashboard } from './components/notches-dashboard/NotchesDashboard';
import { PillarLanding } from './components/notches-dashboard/PillarLanding';
import { EquityOptionsPreview } from './components/notches-dashboard/EquityOptionsPreview';
import { StockSignalsPreview } from './components/notches-dashboard/StockSignalsPreview';
import { MarketsComingSoon } from './components/notches-dashboard/MarketsComingSoon';
import { SignalTestResults } from './components/notches-dashboard/SignalTestResults';
import { SignalPostmortem } from './components/notches-dashboard/SignalPostmortem';
import { N2LandingPage } from './n2/N2LandingPage';
import { NotchesEntrance } from './n2/NotchesEntrance';
import { NotchesManualTradingPage } from './n2/NotchesManualTradingPage';
import { NotchesAutomatedTradingPage } from './n2/NotchesAutomatedTradingPage';
import { NotchesFrameworkPage } from './n2/NotchesFrameworkPage';
import { NotchesBoringBucketPage } from './n2/NotchesBoringBucketPage';
import { NotchesPlansPage } from './n2/NotchesPlansPage';
import { N2ScorecardPage } from './n2/N2ScorecardPage';
import { N2ScorecardModelPage } from './n2/N2ScorecardModelPage';
import { N2ScorecardLogPage } from './n2/N2ScorecardLogPage';
import { N2ScorecardPlaybookPage } from './n2/N2ScorecardPlaybookPage';
import { N2ResearchPage } from './n2/N2ResearchPage';
import { N2ResearchLetterPage } from './n2/N2ResearchLetterPage';
import { N2BriefingsListPage } from './n2/N2BriefingsListPage';
import { N2BriefingPage } from './n2/N2BriefingPage';
import { N2MethodologyPage } from './n2/N2MethodologyPage';
import { N2AboutPage } from './n2/N2AboutPage';
import { N2CalendarReadPage } from './n2/N2CalendarReadPage';
import { AlgoSignals } from './components/algo/AlgoSignals';
import { AlgoTrades } from './components/algo/AlgoTrades';
import { AlgoAudit } from './components/algo/AlgoAudit';
import { AlgoPortfolio } from './components/algo/AlgoPortfolio';
import OptionsEngines from './components/options-trader/OptionsEngines';
import OptionsEnginesV2 from './components/options-trader-v2/OptionsEnginesV2';
import OptionsTradesV2 from './components/options-trader-v2/OptionsTradesV2';
import OptionsAnalyticsV2 from './components/options-trader-v2/OptionsAnalyticsV2';
import OptionsAuditV2 from './components/options-trader-v2/OptionsAuditV2';
import { BoringBucket } from './components/boring-bucket/BoringBucket';
import RiskPage from './components/risk/RiskPage';
import { FounderTrades } from './components/boring-bucket/FounderTrades';
import { Mentoring } from './components/boring-bucket/Mentoring';
import { CapitalPlan } from './components/boring-bucket/CapitalPlan';
import { BoringBucketJoin } from './components/boring-bucket/BoringBucketJoin';
import { DiscourseSso } from './components/boring-bucket/DiscourseSso';
import { FounderConsole } from './components/boring-bucket/FounderConsole';
import OptionsTrades from './components/options-trader/OptionsTrades';
import OptionsAnalytics from './components/options-trader/OptionsAnalytics';
import OptionsAudit from './components/options-trader/OptionsAudit';
import { TradeWithMe } from './components/TradeWithMe';
import { SpyWeeklyReport, EsWeeklyReport, MesWeeklyReport } from './components/indices/IndexWeeklyReport';
import { SpxLanding } from './components/spx/SpxLanding';
import { SpxSignals } from './components/spx/SpxSignals';
import { SpxChatGpt } from './components/spx/SpxChatGpt';
import { SpxAudit } from './components/spx/SpxAudit';
import { SpxReport } from './components/spx/SpxReport';
import { SpxSignalsV2 } from './components/spx/SpxSignalsV2';
import { SpxWeeklyReport } from './components/spx/SpxWeeklyReport';
import { IndexLanding } from './components/indices/IndexLanding';
import { SignalLanding } from './components/indices/SignalLanding';
import { N2InfluencerPage } from './n2/N2InfluencerPage';
import { N2EnterprisePage } from './n2/N2EnterprisePage';
import { ContactPage } from './components/ContactPage';
import { PartnershipPage } from './components/PartnershipPage';
// WealthBuildingPage + TradingPage retired 2026-07-28 as part of the B2B
// pivot -- retail-focused destinations replaced by /n2 landing. Routes
// below now redirect to /n2. Components remain in components/ until we
// verify no live traffic, then delete.
import { IndexSignals } from './components/indices/IndexSignals';
import { IndexChatGpt } from './components/indices/IndexChatGpt';
import { IndexAudit } from './components/indices/IndexAudit';
import { FuturesSignals } from './components/futures/FuturesSignals';
import { FuturesChatGpt } from './components/futures/FuturesChatGpt';
import { FuturesAudit } from './components/futures/FuturesAudit';
import { AiAnalysisV2 } from './components/shared/AiAnalysisV2';
import { IndexSignalsV2 } from './components/indices/IndexSignalsV2';
import { FuturesSignalsV2 } from './components/futures/FuturesSignalsV2';
import { Terms } from './components/Terms';
import { Privacy } from './components/Privacy';
import { LandingPage } from './components/LandingPage';
import { AdminLogin } from './components/admin/AdminLogin';
import { AdminDashboard } from './components/admin/AdminDashboard';
import './App.css';

// Protected Route component
// Normalize a caller-supplied `?redirect=<path>` to a safe in-app path.
// Returns null when the value could produce an open-redirect (protocol-
// relative host via // or \\, javascript:/data:/vbscript: scheme, or any
// character outside a small path/query safe list). The N2 auth links pass
// values like `n2/briefings/2026-07-27/eod`; anything else is untrusted.
export function safeRedirectPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const stripped = raw.replace(/^[/\\]+/, '');
  if (!stripped) return null;
  // Reject anything with a scheme (`scheme:` before the first `/`).
  const firstSlash = stripped.indexOf('/');
  const beforeSlash = firstSlash === -1 ? stripped : stripped.slice(0, firstSlash);
  if (beforeSlash.includes(':')) return null;
  // Whitelist path characters -- letters, digits, common URL-safe punct,
  // percent-encoding. No backslashes, no control chars, no whitespace.
  if (!/^[A-Za-z0-9_\-./?&=#%~+,;'!*()@$:]*$/.test(stripped)) return null;
  return '/' + stripped;
}

const ProtectedRoute: React.FC<{ children: React.ReactNode; requireVerified?: boolean }> = ({
  children,
  requireVerified = false
}) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // If the route requires verification and user is not verified, redirect to verify-email
  if (requireVerified && user && !user.emailVerified) {
    return <Navigate to="/verify-email" />;
  }

  return <>{children}</>;
};

// Public Route component (redirect to appropriate page if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (isAuthenticated) {
    // If authenticated but not verified, go to verify-email
    if (user && !user.emailVerified) {
      return <Navigate to="/verify-email" />;
    }
    // Respect ?redirect=<path> if the caller passed one (e.g. /n2 pages
    // hand it in so signed-in users return to the briefing they came from
    // instead of getting dumped on the dashboard).
    const safe = safeRedirectPath(new URLSearchParams(location.search).get('redirect'));
    if (safe) return <Navigate to={safe} replace />;
    // Notches Dashboard is the default landing surface (2026-05-25).
    return <Navigate to="/notches/dashboard" />;
  }

  return <>{children}</>;
};

// Default redirect component that handles both authenticated and unauthenticated users
const DefaultRedirect: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Public entry point is /n2 (Notches by WFS) so unauthenticated
    // visitors get the consistent /n2 design system across the whole
    // marketing surface -- landing + briefings + scorecard + research
    // + methodology. The old LandingPage lives on at /old-landing (kept
    // reachable for a migration window; drop after subscribers migrate).
    return <Navigate to="/n2" replace />;
  }

  // If authenticated but not verified, go to verify-email
  if (user && !user.emailVerified) {
    return <Navigate to="/verify-email" />;
  }

  // Notches Dashboard is the default landing surface (2026-05-25).
  return <Navigate to="/notches/dashboard" />;
};

const AuthenticatedChatWidget: React.FC = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <ChatWidget /> : null;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <ZoneProvider>
        <div className="App">
          <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={<ForgotPassword />}
            />

            {/* Public information pages */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route
              path="/learn/4-bucket-system"
              element={
                <ProtectedRoute requireVerified={false}>
                  <FourBucketExplainer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/learn/5-pillars"
              element={
                <ProtectedRoute requireVerified={false}>
                  <FivePillarsExplainer />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/verify-email"
              element={
                <ProtectedRoute>
                  <VerifyEmail />
                </ProtectedRoute>
              }
            />
            {/* Alert setup (hourly/daily email opt-in) retired 2026-10-12 with those emails. */}
            <Route path="/subscription-setup" element={<Navigate to="/notches/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requireVerified={false}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute requireVerified={true}>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-info"
              element={
                <ProtectedRoute requireVerified={true}>
                  <EmailInfo />
                </ProtectedRoute>
              }
            />
            {/* Symbol Lists (email alert preferences) retired 2026-10-12 with the IV + daily report emails. */}
            <Route path="/list-preferences" element={<Navigate to="/notches" replace />} />
            <Route
              path="/upgrade"
              element={
                <ProtectedRoute requireVerified={true}>
                  <Upgrade />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription/success"
              element={
                <ProtectedRoute requireVerified={true}>
                  <SubscriptionSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription/continue"
              element={
                <ProtectedRoute requireVerified={true}>
                  <CheckoutContinue />
                </ProtectedRoute>
              }
            />
            {/* Options research (formerly "Trade Signals") - v1 retired 2026-06-13.
                Canonical URL is /options-research per vocab migration D3
                (2026-07-22). Phase 5 flipped 2026-07-27: all legacy paths
                now <Navigate replace> to the canonical URL. Beacon on
                page-view-events-prod isn't wired for these paths (only
                /n2/*), so the flip is behavior-safe rather than data-
                proven: dual-mount and Navigate rendered identical content;
                switching to Navigate just canonicalizes the URL bar for
                bookmark rehydration. Rollback: revert this hunk. */}
            <Route
              path="/options-research"
              element={
                <ProtectedRoute requireVerified={true}>
                  <TradeSignalsV2 />
                </ProtectedRoute>
              }
            />
            <Route path="/trade-signals/v2" element={<Navigate to="/options-research" replace />} />
            <Route path="/trade-signals" element={<Navigate to="/options-research" replace />} />
            <Route path="/options-equity-signals" element={<Navigate to="/options-research" replace />} />
            {/* TT Stocks auto-trader — Bobby-only equity engines (paper + live) */}
            <Route
              path="/tt-stock/engines"
              element={
                <ProtectedRoute requireVerified={true}>
                  <TtStockEngines />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tt-stock/trades"
              element={
                <ProtectedRoute requireVerified={true}>
                  <TtStockTrades />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tt-stock/audit"
              element={
                <ProtectedRoute requireVerified={true}>
                  <TtStockAudit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tt-stock/dashboard"
              element={
                <ProtectedRoute requireVerified={true}>
                  <TtStockDashboard />
                </ProtectedRoute>
              }
            />
            {/* Portfolio Risk Analyzer — BB-only (Phase A: single-account, manual) */}
            <Route
              path="/risk"
              element={
                <ProtectedRoute requireVerified={true}>
                  <RiskPage />
                </ProtectedRoute>
              }
            />

            {/* The Boring Bucket — mentored membership section. /join is a
                plain route (not ProtectedRoute) so the onboarding page can
                handle logged-out applicants itself and capture the signup
                token before any auth bounce. */}
            <Route
              path="/boring-bucket"
              element={
                <ProtectedRoute requireVerified={true}>
                  <BoringBucket />
                </ProtectedRoute>
              }
            />
            <Route
              path="/boring-bucket/founder-trades"
              element={
                <ProtectedRoute requireVerified={true}>
                  <FounderTrades />
                </ProtectedRoute>
              }
            />
            <Route
              path="/boring-bucket/mentoring"
              element={
                <ProtectedRoute requireVerified={true}>
                  <Mentoring />
                </ProtectedRoute>
              }
            />
            <Route
              path="/boring-bucket/capital-plan"
              element={
                <ProtectedRoute requireVerified={true}>
                  <CapitalPlan />
                </ProtectedRoute>
              }
            />
            <Route path="/boring-bucket/join" element={<BoringBucketJoin />} />
            <Route path="/discourse-sso" element={<DiscourseSso />} />
            <Route
              path="/boring-bucket/founder-console"
              element={
                <ProtectedRoute requireVerified={true}>
                  <FounderConsole />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fire-calculator"
              element={
                <ProtectedRoute requireVerified={true}>
                  <FireCalculator />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lifecycle-investment"
              element={
                <ProtectedRoute requireVerified={true}>
                  <LifecycleInvestment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/net-worth"
              element={
                <ProtectedRoute requireVerified={true}>
                  <NetWorth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/playbook"
              element={
                <ProtectedRoute requireVerified={true}>
                  <Playbook />
                </ProtectedRoute>
              }
            />
            {/* /book is intentionally public -- it's a marketing surface
                that visitors arrive at from IG, Stories, and email links
                before they sign up. */}
            <Route path="/book" element={<BookPage />} />
            {/* /links is the public QR-code hub — book, book tools, WFS,
                and social profiles. Marketed on IG bio + print. */}
            <Route path="/links" element={<LinksPage />} />
            <Route
              path="/dca-tool"
              element={
                <ProtectedRoute requireVerified={true}>
                  <DcaTool />
                </ProtectedRoute>
              }
            />
            {/* Stock research (formerly "Stock Signals") - Phase 5 flipped
                2026-07-27. Canonical URL is /stock-research per vocab
                migration D3 (2026-07-22); legacy paths now <Navigate
                replace> to the canonical. Same rationale as the
                options-research block above -- dual-mount and Navigate
                render identical content, this just canonicalizes the URL
                bar. Rollback: revert this hunk. */}
            <Route
              path="/stock-research"
              element={
                <ProtectedRoute requireVerified={true}>
                  <StockSignals />
                </ProtectedRoute>
              }
            />
            <Route path="/stock-signals" element={<Navigate to="/stock-research" replace />} />
            <Route path="/equity-signals" element={<Navigate to="/stock-research" replace />} />
            {/* Daily IV + stock daily reports retired 2026-10-12 (replaced by Notches briefings). */}
            <Route path="/stock-daily-report" element={<Navigate to="/notches" replace />} />
            <Route
              path="/bucket-visualizer"
              element={
                <ProtectedRoute requireVerified={true}>
                  <BucketVisualizer />
                </ProtectedRoute>
              }
            />
            <Route path="/daily-monitor" element={<Navigate to="/notches" replace />} />

            {/* Notches v1 (parallel deployment alongside /spx/v2 + /indices/v2) */}
            <Route
              path="/notches"
              element={
                <ProtectedRoute requireVerified={true}>
                  <NotchesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notches/audit"
              element={
                <ProtectedRoute requireVerified={true}>
                  <NotchesAudit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notches/events"
              element={
                <ProtectedRoute requireVerified={true}>
                  <NotchesEventsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notches/reports"
              element={
                <ProtectedRoute requireVerified={true}>
                  <NotchesReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notches/auto-traders/stress-test"
              element={
                <ProtectedRoute requireVerified={true}>
                  <NotchesStressTest />
                </ProtectedRoute>
              }
            />
            {/* Notches Dashboard - preview build, entitlement-gated inside the component */}
            <Route
              path="/notches/dashboard"
              element={
                <ProtectedRoute requireVerified={true}>
                  <NotchesDashboard />
                </ProtectedRoute>
              }
            />
            {/* Notches Dashboard pillar landings (preview) */}
            <Route path="/notches/dashboard/signals" element={
              <ProtectedRoute requireVerified={true}><PillarLanding pillar="signals" /></ProtectedRoute>
            } />
            <Route path="/notches/dashboard/markets" element={
              <ProtectedRoute requireVerified={true}><PillarLanding pillar="markets" /></ProtectedRoute>
            } />
            <Route path="/notches/dashboard/reports" element={
              <ProtectedRoute requireVerified={true}><PillarLanding pillar="reports" /></ProtectedRoute>
            } />
            <Route path="/notches/dashboard/auto-traders" element={
              <ProtectedRoute requireVerified={true}><PillarLanding pillar="auto-traders" /></ProtectedRoute>
            } />
            {/* Per-symbol signal pages route into the existing /notches page
                with a ?group= filter - no parallel SymbolSignalsPage. Equity
                + stocks still deeplink to the legacy product surfaces. */}
            <Route path="/markets" element={
              <ProtectedRoute requireVerified={true}><MarketsComingSoon /></ProtectedRoute>
            } />
            {/* Deep research pages — gated. Public bridge on the landing page
                shows the CTA with a "Sign-in required" indicator; the pages
                themselves are behind auth to reserve as subscriber value. */}
            <Route path="/markets/test-results" element={
              <ProtectedRoute requireVerified={true}><SignalTestResults /></ProtectedRoute>
            } />
            <Route path="/markets/postmortem" element={
              <ProtectedRoute requireVerified={true}><SignalPostmortem /></ProtectedRoute>
            } />

            {/* /n2 is now the public entry point. `/` redirects here for
                unauthenticated visitors (see DefaultRedirect). The previous
                LandingPage lives on at /old-landing for any bookmarked URL
                until we confirm no live traffic to it, then it goes away. */}
            <Route path="/old-landing" element={<LandingPage />} />
            <Route path="/n2" element={<NotchesEntrance />} />
            <Route path="/n2/manual-trading" element={<NotchesManualTradingPage />} />
            <Route path="/n2/automated-trading" element={<NotchesAutomatedTradingPage />} />
            <Route path="/n2/framework" element={<NotchesFrameworkPage />} />
            <Route path="/n2/boring-bucket" element={<NotchesBoringBucketPage />} />
            <Route path="/n2/plans" element={<NotchesPlansPage />} />
            <Route path="/n2/full-landing" element={<N2LandingPage />} />
            <Route path="/n2/scorecard" element={<N2ScorecardPage />} />
            <Route path="/n2/scorecard/log" element={<N2ScorecardLogPage />} />
            <Route path="/n2/scorecard/playbook" element={<N2ScorecardPlaybookPage />} />
            <Route path="/n2/scorecard/:model" element={<N2ScorecardModelPage />} />
            {/* Public-facing alias -- promo copy points to /scorecard so the
                internal /n2 name doesn't leak into customer-visible URLs.
                Dual-mounted (not <Navigate>) so the URL bar stays clean. */}
            <Route path="/scorecard" element={<N2ScorecardPage />} />
            <Route path="/n2/research" element={<N2ResearchPage />} />
            <Route path="/n2/research/:weekIso" element={<N2ResearchLetterPage />} />
            <Route path="/n2/briefings" element={<N2BriefingsListPage />} />
            <Route path="/n2/briefings/:date" element={<N2BriefingPage />} />
            <Route path="/n2/briefings/:date/:variant" element={<N2BriefingPage />} />
            {/* Short bio-link alias -- keeps the URL memorable on IG/TikTok profiles */}
            <Route path="/read" element={<N2BriefingPage />} />
            {/* Premier-gated (calendarNotchesEnabled) -- page itself handles the
                auth/entitlement gate; nav link is hidden for non-enabled users. */}
            <Route path="/n2/calendar-notches" element={<N2CalendarReadPage />} />
            <Route path="/n2/methodology" element={<N2MethodologyPage />} />
            <Route path="/n2/about" element={<N2AboutPage />} />
            <Route path="/notches/dashboard/signals/options/equity" element={
              <ProtectedRoute requireVerified={true}><EquityOptionsPreview /></ProtectedRoute>
            } />
            <Route path="/notches/dashboard/signals/stocks" element={
              <ProtectedRoute requireVerified={true}><StockSignalsPreview /></ProtectedRoute>
            } />

            {/* Phase 4: existing surfaces moved under the /notches/* tree.
                Each route renders the SAME component as its legacy URL so the
                old URL keeps working as an alias (no redirect, dual routes).
                Per docs/wiki/decisions/2026-05-13-notches-product-umbrella-ia.md. */}
            <Route path="/notches/signals/stocks" element={
              <ProtectedRoute requireVerified={true}><StockSignals /></ProtectedRoute>
            } />
            <Route path="/notches/signals/options/equity" element={<Navigate to="/options-research" replace />} />
            <Route path="/notches/reports/iv" element={<Navigate to="/notches" replace />} />
            <Route path="/notches/reports/daily" element={<Navigate to="/notches" replace />} />
            <Route path="/notches/reports/preferences" element={<Navigate to="/notches" replace />} />
            <Route path="/notches/auto-traders/tastytrade" element={
              <ProtectedRoute requireVerified={true}><OptionsEngines /></ProtectedRoute>
            } />
            <Route path="/notches/auto-traders/tastytrade/trades" element={
              <ProtectedRoute requireVerified={true}><OptionsTrades /></ProtectedRoute>
            } />
            <Route path="/notches/auto-traders/tastytrade/analytics" element={
              <ProtectedRoute requireVerified={true}><OptionsAnalytics /></ProtectedRoute>
            } />
            <Route path="/notches/auto-traders/tastytrade/audit" element={
              <ProtectedRoute requireVerified={true}><OptionsAudit /></ProtectedRoute>
            } />
            <Route path="/notches/auto-traders/alpaca" element={
              <ProtectedRoute requireVerified={true}><AlgoEngines /></ProtectedRoute>
            } />
            <Route path="/notches/auto-traders/alpaca/signals" element={
              <ProtectedRoute requireVerified={true}><AlgoSignals /></ProtectedRoute>
            } />
            <Route path="/notches/auto-traders/alpaca/trades" element={
              <ProtectedRoute requireVerified={true}><AlgoTrades /></ProtectedRoute>
            } />
            <Route path="/notches/auto-traders/alpaca/portfolio" element={
              <ProtectedRoute requireVerified={true}><AlgoPortfolio /></ProtectedRoute>
            } />
            <Route path="/notches/auto-traders/alpaca/audit" element={
              <ProtectedRoute requireVerified={true}><AlgoAudit /></ProtectedRoute>
            } />
            {/* Algo Trading routes */}
            <Route
              path="/algo/engines"
              element={
                <ProtectedRoute requireVerified={true}>
                  <AlgoEngines />
                </ProtectedRoute>
              }
            />
            <Route
              path="/algo/signals"
              element={
                <ProtectedRoute requireVerified={true}>
                  <AlgoSignals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/algo/trades"
              element={
                <ProtectedRoute requireVerified={true}>
                  <AlgoTrades />
                </ProtectedRoute>
              }
            />
            <Route
              path="/algo/portfolio"
              element={
                <ProtectedRoute requireVerified={true}>
                  <AlgoPortfolio />
                </ProtectedRoute>
              }
            />
            <Route
              path="/algo/audit"
              element={
                <ProtectedRoute requireVerified={true}>
                  <AlgoAudit />
                </ProtectedRoute>
              }
            />

            {/* Options Auto-Trader routes */}
            {/* TT Options Trader v2 (paper) */}
            <Route path="/engines-v2" element={<Navigate to="/options-trader-v2/engines" replace />} />
            <Route path="/options-trader-v2/engines" element={
              <ProtectedRoute requireVerified={true}><OptionsEnginesV2 /></ProtectedRoute>
            } />
            <Route path="/options-trader-v2/trades" element={
              <ProtectedRoute requireVerified={true}><OptionsTradesV2 /></ProtectedRoute>
            } />
            <Route path="/options-trader-v2/analytics" element={
              <ProtectedRoute requireVerified={true}><OptionsAnalyticsV2 /></ProtectedRoute>
            } />
            <Route path="/options-trader-v2/audit" element={
              <ProtectedRoute requireVerified={true}><OptionsAuditV2 /></ProtectedRoute>
            } />
            <Route
              path="/options-trader/engines"
              element={
                <ProtectedRoute requireVerified={true}>
                  <OptionsEngines />
                </ProtectedRoute>
              }
            />
            <Route
              path="/options-trader/trades"
              element={
                <ProtectedRoute requireVerified={true}>
                  <OptionsTrades />
                </ProtectedRoute>
              }
            />
            <Route
              path="/options-trader/analytics"
              element={
                <ProtectedRoute requireVerified={true}>
                  <OptionsAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/options-trader/audit"
              element={
                <ProtectedRoute requireVerified={true}>
                  <OptionsAudit />
                </ProtectedRoute>
              }
            />

            {/* Trade With Me */}
            <Route
              path="/trade-with-me"
              element={
                <ProtectedRoute requireVerified={true}>
                  <TradeWithMe />
                </ProtectedRoute>
              }
            />

            {/* SPX routes */}
            <Route path="/spx" element={<SpxLanding />} />
            <Route
              path="/spx/signals"
              element={
                <ProtectedRoute requireVerified={true}>
                  <SpxSignals />
                </ProtectedRoute>
              }
            />
            {/* SPX V2-backed routes retired 2026-05-25 -- redirect to Notches.
                Preserves bookmarks (Sandip etc.) without keeping the V2 surface alive. */}
            <Route path="/spx/ai-analysis" element={<Navigate to="/notches?symbol=SPX" replace />} />
            <Route path="/spx/audit"       element={<Navigate to="/notches?symbol=SPX" replace />} />
            <Route
              path="/spx/report"
              element={
                <ProtectedRoute requireVerified={false}>
                  <SpxReport />
                </ProtectedRoute>
              }
            />

            {/* SPX V2 retired 2026-05-25 -- redirect to Notches. */}
            <Route path="/spx/v2"         element={<Navigate to="/notches?symbol=SPX" replace />} />
            <Route path="/spx/v2-weekly"  element={<Navigate to="/notches?symbol=SPX" replace />} />
            <Route
              path="/indices/v2-weekly"
              element={
                <ProtectedRoute requireVerified={true}>
                  <SpyWeeklyReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futures/v2-weekly-es"
              element={
                <ProtectedRoute requireVerified={true}>
                  <EsWeeklyReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futures/v2-weekly-mes"
              element={
                <ProtectedRoute requireVerified={true}>
                  <MesWeeklyReport />
                </ProtectedRoute>
              }
            />

            {/* Individual product landing pages (Notches per-symbol) */}
            <Route path="/spy" element={<SignalLanding product="spy" />} />
            <Route path="/es" element={<SignalLanding product="es" />} />
            <Route path="/mes" element={<SignalLanding product="mes" />} />
            {/* Legacy futures URLs -> canonical (vocab migration follow-on 2026-07-28) */}
            <Route path="/es-signals" element={<Navigate to="/es" replace />} />
            <Route path="/mes-signals" element={<Navigate to="/mes" replace />} />
            <Route path="/influencer" element={<N2InfluencerPage />} />
            <Route path="/enterprise" element={<N2EnterprisePage />} />
            <Route path="/contact" element={<ContactPage />} />
            {/* /contact-sales was never a real route -- external links still hit it, redirect to /contact */}
            <Route path="/contact-sales" element={<Navigate to="/contact" replace />} />
            <Route path="/partnership-apply" element={<PartnershipPage />} />
            {/* Retail destinations retired 2026-07-28 (B2B pivot) -- redirect to /n2 */}
            <Route path="/wealth-building" element={<Navigate to="/n2" replace />} />
            <Route path="/trading" element={<Navigate to="/n2" replace />} />

            {/* Index Options routes */}
            <Route path="/indices" element={<IndexLanding />} />
            <Route
              path="/indices/signals"
              element={
                <ProtectedRoute requireVerified={true}>
                  <IndexSignals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/indices/ai-analysis"
              element={
                <ProtectedRoute requireVerified={true}>
                  <AiAnalysisV2 apiPath="/indices/v2?symbol=SPY" title="SPY AI Analysis" symbol="SPY" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/indices/audit"
              element={
                <ProtectedRoute requireVerified={true}>
                  <IndexAudit />
                </ProtectedRoute>
              }
            />

            {/* Index Options V2 */}
            <Route
              path="/indices/v2"
              element={
                <ProtectedRoute requireVerified={true}>
                  <IndexSignalsV2 />
                </ProtectedRoute>
              }
            />

            {/* Futures Options V2 */}
            <Route
              path="/futures/v2"
              element={
                <ProtectedRoute requireVerified={true}>
                  <FuturesSignalsV2 />
                </ProtectedRoute>
              }
            />

            {/* Futures Options routes */}
            <Route
              path="/futures/signals"
              element={
                <ProtectedRoute requireVerified={true}>
                  <FuturesSignals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futures/ai-analysis"
              element={
                <ProtectedRoute requireVerified={true}>
                  <AiAnalysisV2 apiPath="/indices/v2?symbol=ES" title="ES/MES AI Analysis" symbol="ES" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/futures/audit"
              element={
                <ProtectedRoute requireVerified={true}>
                  <FuturesAudit />
                </ProtectedRoute>
              }
            />

            {/* Support routes */}
            <Route
              path="/help"
              element={
                <ProtectedRoute requireVerified={false}>
                  <HelpDocs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contact-support"
              element={
                <ProtectedRoute requireVerified={true}>
                  <ContactSupport />
                </ProtectedRoute>
              }
            />

            {/* Admin routes (separate auth — no ProtectedRoute) */}
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />

            {/* Default redirect */}
            <Route path="/" element={<DefaultRedirect />} />
          </Routes>
          <AuthenticatedChatWidget />
        </div>
        </ZoneProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
